import axios from 'axios';
import querystring from 'querystring';
import { platformPublicApiBaseUrl } from './config';
import { ApiPaginatedResults, Attribute, Item, OwnedNft, TokenOwnership } from './types';
import { OwnedItem } from 'utils/types';

export const fetchCollectionStats = async (collectionId: string) => {
  const { data } = await axios.get(
    `${platformPublicApiBaseUrl}/v1/collections/${collectionId}/stats`
  );
  return data;
};

export const fetchOwnedItems = async (
  orgId: string,
  address: string,
  chainIds: string[]
): Promise<OwnedItem[]> => {
  // @ts-ignore
  const params = querystring.encode({
    network_ids: chainIds,
  });
  const { data } = await axios.get(
    `${platformPublicApiBaseUrl}/beta/eulerbeats/tokens/${orgId}/${address}?${params}`
  );
  // @ts-ignore
  return data.results;
};

export const fetchOwnedNft = async (contract: string, tokenId: string | number, chain: number) => {
  const { results } = await fetchOwnedNfts(undefined, [chain], [contract], [String(tokenId)], 1);
  return results[0];
};

export const fetchOwnedNfts = async (
  account?: string,
  networkIds?: number[],
  contracts?: string[],
  tokenIds?: string[],
  limit?: number,
  cursor?: string
): Promise<ApiPaginatedResults<OwnedNft>> => {
  const params = new URLSearchParams({});

  if (account) {
    params.append('accounts', account);
  }

  if (contracts) {
    contracts.forEach(c => params.append('contract_address', c));
  }

  if (tokenIds) {
    tokenIds.forEach(t => params.append('token_id', t));
  }

  if (networkIds) {
    networkIds.forEach(c => params.append('network_ids', String(c)));
  }

  if (cursor) {
    params.append('cursor', cursor);
  }

  if (limit) {
    params.append('limit', `${limit}`);
  }

  const { data }: { data: ApiPaginatedResults<TokenOwnership> } = await axios.get(
    `${platformPublicApiBaseUrl}/beta/eulerbeats/ownership?${params}`
  );

  const results: OwnedNft[] = data.results.map(ownership => {
    const result: OwnedNft = {
      contract_address: ownership.contract_address,
      owner: ownership.owner,
      chain: ownership.network_id,
      quantity: String(ownership.quantity),
      token_id: ownership.token_id,
      metadata: {
        name: ownership.attributes.name,
        image: ownership.media.image?.original ?? '',
        attributes: ownership.attributes,
      },
    };
    return result;
  });

  return { ...data, results };
};

// The public api has issues with external collections
// so we need to use an equivalent to admin api.
export const fetchExternalCollectionItems = async (
  collectionId: string,
  attributes: Attribute[],
  tokenIds?: string[],
  limit?: string | number,
  cursor?: string
): Promise<ApiPaginatedResults<Item>> => {
  let searchParams = new URLSearchParams();
  searchParams.set('collection_id', collectionId);
  searchParams.set('limit', String(limit || 24));
  if (cursor) {
    searchParams.set('cursor', cursor);
  }

  if (tokenIds) {
    tokenIds.forEach(t => searchParams.append('token_ids', t));
  }

  for (const attribute of attributes) {
    searchParams.append('attributes', `${attribute.name}:${attribute.value}`);
  }

  const { data } = await axios.get(
    `${platformPublicApiBaseUrl}/beta/eulerbeats/items?${searchParams}`
  );
  return data;
};
